import { COOKIE_ACCEPTED, COOKIE_DECLINED, localStorageKeys } from 'constants';

/**
 * Utility for checking if Google Tag Manager is loaded
 */

/**
 * Checks if Google Tag Manager is properly loaded on the page
 * @returns {boolean} True if GTM is loaded, false otherwise
 */
export const isGTMLoaded = () => {
  // Check if dataLayer exists
  if (typeof window.dataLayer !== 'undefined') {
    //console.log('GTM dataLayer found:', window.dataLayer);
    return true;
  }

  // Check if GTM script is in document
  const scripts = document.querySelectorAll('script');
  for (let i = 0; i < scripts.length; i++) {
    if (
      scripts[i].src &&
      scripts[i].src.includes('googletagmanager.com/gtm.js')
    ) {
      // console.log('GTM script found:', scripts[i].src);
      return true;
    }
  }

  //console.error('GTM not found on page');
  return false;
};

/**
 * Manually pushes a page view event to dataLayer
 * @param {string} path Page path
 */
export const trackPageView = (path) => {
  if (typeof window.dataLayer !== 'undefined') {
    window.dataLayer.push({
      event: 'page_view',
      page: path || window.location.pathname,
    });
    //console.log('Page view tracked:', path || window.location.pathname);
  } else {
    //console.error('Cannot track page view: dataLayer not found');
  }
};

/**
 * Track event directly with GA4 gtag
 * @param {string} eventName Name of the event
 * @param {object} eventParams Event parameters
 */
export const trackGtag = (eventName, eventParams = {}) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, eventParams);
    //console.log('Direct GA4 event tracked:', eventName, eventParams);
    return true;
  }

  //console.error('Cannot track event: gtag not found');
  return false;
};

/**
 * Track page view directly with GA4 gtag
 * @param {string} path Page path
 */
export const trackGtagPageView = (path) => {
  const pagePath = path || window.location.pathname;

  if (typeof window.gtag !== 'undefined') {
    window.gtag('config', 'G-SY2CF6L6PR', {
      page_path: pagePath,
    });
    //console.log('Direct GA4 pageview tracked:', pagePath);
    return true;
  }

  //console.error('Cannot track pageview: gtag not found');
  return false;
};

/**
 * Google Ads conversion actions (Ziele > Conversions in Google Ads).
 * Only completed actions are sent as conversions; a click on a Calendly link
 * is tracked as a plain GA4 event (`calendly_link_click`) and is not one.
 */
export const GOOGLE_ADS_CONVERSIONS = {
  // „Termin vereinbaren": a demo was actually booked in Calendly
  demoBooked: 'AW-17028698506/G471CIeTp5odEIqj9bc_',
  // „Lead-Formular senden": the contact form was sent successfully
  contactForm: 'AW-17028698506/kXTvCOHczbwaEIqj9bc_',
};

const consentDecision = () => {
  try {
    return window.localStorage.getItem(localStorageKeys.cookieConsent);
  } catch (e) {
    return null;
  }
};

/**
 * Whether the visitor accepted cookies in our banner (src/app/App.jsx).
 * @returns {boolean}
 */
export const hasTrackingConsent = () => consentDecision() === COOKIE_ACCEPTED;

// Conversions that happened while the cookie banner was still unanswered
// (e.g. someone books straight from the header). They are sent if the visitor
// accepts later during this visit and dropped if they decline.
let pendingConversions = [];

const whenConsented = (send) => {
  const decision = consentDecision();
  if (decision === COOKIE_ACCEPTED) return send();
  if (decision !== COOKIE_DECLINED) pendingConversions.push(send);
  return false;
};

/** Sends queued conversions; call right after consent was granted. */
export const flushPendingConversions = () => {
  const queue = pendingConversions;
  pendingConversions = [];
  queue.forEach((send) => send());
};

/** Drops queued conversions; call when the visitor declines cookies. */
export const discardPendingConversions = () => {
  pendingConversions = [];
};

/**
 * A demo was booked in Calendly (popup or the /demo page).
 * @param {string | null} bookingId Calendly invitee id; used as transaction_id
 *   so Google Ads counts each booking only once.
 * @returns {boolean} Whether the events were handed to gtag now (false while
 *   waiting for the cookie banner or without consent)
 */
export const trackDemoBooked = (bookingId) =>
  whenConsented(() => {
    const conversion = {
      send_to: GOOGLE_ADS_CONVERSIONS.demoBooked,
      value: 1.0,
      currency: 'EUR',
    };
    if (bookingId) conversion.transaction_id = bookingId;

    trackGtag('conversion', conversion);
    return trackGtag('demo_booked', { method: 'calendly' });
  });

/**
 * The contact form was sent successfully.
 * @returns {boolean} Whether the events were handed to gtag
 */
export const trackContactFormLead = () =>
  whenConsented(() => {
    trackGtag('conversion', {
      send_to: GOOGLE_ADS_CONVERSIONS.contactForm,
      value: 1.0,
      currency: 'EUR',
    });
    return trackGtag('generate_lead', { method: 'contact_form' });
  });
